import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild
} from '@angular/core';
import { LeftSidenavButtonComponent, LeftSidenavButton } from './left-sidenav-button/left-sidenav-button.component';
import { DeviceService, IconComponent, IconKey } from '@gains-com/ui-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { AuthService } from '../../core/auth.service';
import { UserViewService } from '../../core/view-states/user-view.service';
import { ShellService } from '../shell.service';
import { Subscription } from 'rxjs';
import { EnvironmentPipe } from '../../pipes/environment.pipe';
import { IntercomClient } from '../../core/intercom-client.service';
import { RouterLink } from '@angular/router';
import { PromotionsView } from '../../core/view-states/promotions-view.service.js';
import { VipViewService } from '../../core/view-states/vip-view.service.js';
import { MerchViewService } from '../../core/view-states/merch-view.service.js';

@Component({
  selector: 'app-left-sidenav',
  imports: [
    LeftSidenavButtonComponent,
    MatButton,
    IconComponent,
    MatIconButton,
    EnvironmentPipe,
    RouterLink,
  ],
  templateUrl: './left-sidenav.component.html',
  styleUrl: './left-sidenav.component.scss',
  host: {
    '[class.collapsed]': 'shell.leftSidenavCollapsed()',
  },
})
export class LeftSidenavComponent implements OnDestroy {
  readonly leftSidenavContent = viewChild<ElementRef>('leftSidenavContent');

  readonly device = inject(DeviceService);
  readonly userView = inject(UserViewService);
  readonly shell = inject(ShellService);
  readonly promotionView = inject(PromotionsView);
  readonly vipView = inject(VipViewService);
  readonly merchView = inject(MerchViewService);

  private readonly _intercom = inject(IntercomClient);
  private readonly _auth = inject(AuthService);
  private readonly _cdr = inject(ChangeDetectorRef);

  // Convert promotions count to signal for computed dependencies and template access
  readonly promotionsCount = toSignal(this.promotionView.promotionsCount$);

  // Count of unclaimed VIP rewards (level-up + weekly) driving the sidenav
  // claim-notification badge. Zero/undefined when signed out, PII not submitted,
  // or vip_enabled === false (VipViewService.state$ is null in all three
  // cases) — naturally in sync with the vip_enabled nav-entry guard below.
  readonly unclaimedVipClaimCount = toSignal(this.vipView.unclaimedVipClaimCount$);

  readonly user = toSignal(this.userView.state$);
  readonly mainButtons = computed(() => {
    const isXs = this.device.isXsScreen();
    const user = this.user();
    const buttons: LeftSidenavButton[] = [
      { iconKey: 'hr:home', label: 'Lobby', link: '/' },
    ];

    // Hide the VIP nav entry when the per-user vip kill-switch is explicitly
    // off. null/undefined (never toggled) and true both mean shown — mirrors
    // the killSwitchGuard semantics on the /vip route itself.
    if (user?.vip_enabled !== false) {
      // Claim-notification badge (unclaimed level-up + pending weekly
      // reward). unclaimedVipClaimCount() is 0/undefined whenever
      // VipViewService.state$ is null, so the badge never shows alongside a
      // hidden/kill-switched nav entry.
      const vipClaimCount = this.unclaimedVipClaimCount();

      buttons.push({
        iconKey: 'hr:vip',
        label: 'VIP Program',
        link: '/vip',
        ...(vipClaimCount && vipClaimCount > 0 ? { count: vipClaimCount } : {}),
      });
    }

    if (user) {
      if (this.merchView.merchExperience()) {
        buttons.push({
          iconUrl: 'icons/merch.svg',
          label: 'Shop',
          link: '/shop',
        });
      }

      buttons.push({
        iconKey: 'hr:refer',
        label: 'Referrals',
        link: '/referrals',
      });

      const promoCount = this.promotionsCount();

      buttons.push({
        iconKey: 'hr:gift',
        label: 'Promotions',
        link: '/promotions',
        ...(promoCount > 0 ? { count: promoCount } : {}),
      });

      if (isXs) {
        buttons.push(
          {
            iconKey: 'hr:settings',
            label: 'Settings',
            link: '/settings/profile',
            iconSize: 20,
          },
          {
            iconKey: 'hr:transactions',
            label: 'Transactions',
            link: '/transactions',
          }
        );
      }
    }

    return buttons;
  });

  readonly gameButtons: LeftSidenavButton[] = [
    {
      iconKey: 'brand:gains_originals',
      label: 'Originals',
      link: '/providers/gains',
    },
    { iconKey: 'hr:slots', label: 'Slot Games', link: '/groups/slots' },
    {
      iconKey: 'hr:dealer',
      label: 'Live Games',
      link: '/groups/live_dealers',
    },
    {
      iconKey: 'hr:table_games',
      label: 'Table Games',
      link: '/groups/table_games',
    },
    { iconKey: 'hr:star', label: 'New Games', link: '/groups/new_games' },
    { iconKey: 'hr:game_shows', label: 'Game Shows', link: '/groups/show' },
  ];

  readonly helpButtons: LeftSidenavButton[] = [
    {
      iconKey: 'hr:support',
      label: 'Live Support',
      link: '',
      onClick: () => this._intercom.open(),
    },
    {
      iconKey: 'hr:security_shield',
      label: 'Privacy Policy',
      link: '/privacy-policy',
    },
    {
      iconKey: 'hr:document',
      label: 'Terms of Service',
      link: '/terms-and-conditions',
    },
    {
      iconKey: 'hr:gift',
      label: 'Sweepstakes Rules',
      link: '/sweepstakes-rules',
    },
    {
      iconKey: 'hr:user_circle',
      label: 'Responsible Social Gaming',
      link: '/responsible-gaming',
    },
    {
      iconKey: 'hr:envelope',
      label: 'Postal Request Code',
      link: '/postal-request-code',
    },
  ];

  /** Tracks whether to show the scroll blur indicator */
  readonly showScrollIndicator = signal(false);

  _leftSidenavSubscription = Subscription.EMPTY;

  constructor() {
    afterNextRender(() => {
      this._leftSidenavSubscription = this.shell.leftSidenavIsOpen$.subscribe(
        () => {
          this.leftSidenavContent()?.nativeElement.scrollTo({
            top: 0,
            behavior: 'instant',
          });
        }
      );

      // Check initial scroll state after content renders
      this._checkScrollIndicator();
    });

    // Re-check scroll indicator after collapse/expand transition completes
    effect(() => {
      // Track the collapsed state to trigger on changes
      this.shell.leftSidenavCollapsed();

      // Wait for CSS transition to complete (300ms) before re-checking
      setTimeout(() => this._checkScrollIndicator(), 300);
    });
  }

  ngOnDestroy() {
    this._leftSidenavSubscription.unsubscribe();
  }

  async signOut() {
    await this._auth.signOut();
    this.shell.toggleLeftSidenav();
    this._cdr.markForCheck();
  }

  /** Handles scroll events to update the blur indicator visibility */
  onScroll(_event: Event) {
    this._checkScrollIndicator();
  }

  /** Checks if content is scrollable and updates the indicator */
  private _checkScrollIndicator() {
    const el = this.leftSidenavContent()?.nativeElement;
    if (!el) return;

    // Show indicator if there's more content to scroll (not at bottom)
    const hasMoreToScroll =
      el.scrollHeight - el.scrollTop - el.clientHeight > 1;
    this.showScrollIndicator.set(hasMoreToScroll);
  }
}

export interface SidenavLink {
  iconKey: IconKey;
  link: string;
}
